// ペンストロークの入力中プレビュー用 Canvas2D レイヤ。
// - 入力中のストロークは use-pointer-mode から rAF 増分描画される
// - 確定後は通常オブジェクトとして保存・描画し、このレイヤは消去する
// - 親（CanvasStage）から canvas DOM へアクセスできるよう ref で実体を取得する
//
// React 19 では関数コンポーネントが直接 ref prop を受け取れるため、forwardRef は不要。

import { useEffect, useImperativeHandle, useRef, type Ref } from "react";
import { CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX } from "../../lib/canvas-coordinates";

export interface PenLayerHandle {
  readonly canvas: HTMLCanvasElement;
}

interface Props {
  readonly handleRef?: Ref<PenLayerHandle | null> | undefined;
}

export function CanvasPenLayer({ handleRef }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = CANVAS_WIDTH_PX;
    canvas.height = CANVAS_HEIGHT_PX;
  }, []);

  useImperativeHandle<PenLayerHandle | null, PenLayerHandle | null>(
    handleRef,
    () => {
      const canvas = canvasRef.current;
      return canvas ? ({ canvas } satisfies PenLayerHandle) : null;
    },
    []
  );

  return (
    <canvas
      ref={canvasRef}
      data-testid="canvas-pen-layer"
      style={{
        position: "absolute",
        inset: 0,
        width: `${CANVAS_WIDTH_PX}px`,
        height: `${CANVAS_HEIGHT_PX}px`,
        pointerEvents: "none",
      }}
    />
  );
}
